import { useEffect, useRef, useState } from "react";

// Live homepage banner carousel. Same ten effects shown on the admin
// /admin/banner-transitions demo, picked at runtime via the `transition`
// prop. Image-only (no captions) — banner data on the live site doesn't
// carry copy. If a banner has Banner_link, clicks navigate.
//
// Props:
//   slides:     [{ src, link? }]
//   transition: one of CAROUSEL_TRANSITIONS keys
//   intervalMs: per-slide hold (default 5000)
//   onClick:    function(slide) — fired when image clicked
//   className:  extra wrapper class

export const CAROUSEL_TRANSITIONS = [
  { key: "crossfade",      name: "Crossfade" },
  { key: "kenBurns",       name: "Ken Burns" },
  { key: "parallax",       name: "Parallax Caption" },
  { key: "cinematicZoom",  name: "Cinematic Zoom" },
  { key: "curtainSplit",   name: "Curtain Split" },
  { key: "diagonalWipe",   name: "Diagonal Wipe" },
  { key: "mosaicTiles",    name: "Mosaic Tiles" },
  { key: "spotlight",      name: "Spotlight" },
  { key: "blurFade",       name: "Blur Fade" },
  { key: "cardFlip",       name: "3D Card Flip" },
];
export const DEFAULT_TRANSITION = "crossfade";
export const DEFAULT_INTERVAL_MS = 5000;
// Shown in the admin "Time between slides" select. Values are seconds.
export const INTERVAL_OPTIONS = [3, 4, 5, 7, 10, 12, 15, 20, 30];

const useAutoIndex = (length, ms) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (length <= 1) return;
    const t = setInterval(() => setI((p) => (p + 1) % length), ms);
    return () => clearInterval(t);
  }, [length, ms]);
  return i;
};

// Preload every image on mount so the first cycle through the carousel
// doesn't stutter while the browser fetches bytes mid-transition.
const usePreloadImages = (slides) => {
  useEffect(() => {
    (slides || []).forEach((s) => {
      if (!s?.src) return;
      const img = new Image();
      img.src = s.src;
    });
  }, [slides]);
};

export default function BannerCarousel({ slides = [], transition = DEFAULT_TRANSITION, intervalMs = 5000, onClick, className }) {
  usePreloadImages(slides);
  if (!slides || slides.length === 0) return null;

  const Cmp = TRANSITION_MAP[transition] || TRANSITION_MAP[DEFAULT_TRANSITION];
  return (
    <div className={className} style={{ width: "100%" }}>
      <style>{KEYFRAMES}</style>
      <Cmp slides={slides} intervalMs={intervalMs} onClick={onClick} />
    </div>
  );
}

// Desktop = hero filling the visible area below the header. Header
// stack on >=992px is offer bar (~40) + logo row (~80) + nav row (~50)
// = ~170px, so we subtract 180px to leave a small reveal of the next
// section as a scroll affordance. 100dvh is used where supported so
// the calc tracks dynamic browser-UI changes; falls back to vh.
// Mobile keeps the 16:5 aspect so the banner doesn't dominate phones.
const KEYFRAMES = `
@keyframes bannerKbA { from { transform: scale(1) translate(0,0); } to { transform: scale(1.12) translate(-2%, -1.5%); } }
@keyframes bannerKbB { from { transform: scale(1) translate(0,0); } to { transform: scale(1.12) translate(2%, 1.5%); } }
@keyframes bannerMosaicIn { from { transform: scale(1); opacity: 1; } to { transform: scale(0); opacity: 0; } }
.tdc-banner-stage { height: calc(100vh - 130px); height: calc(100dvh - 130px); }
/* Mobile/tablet: 16:9 hero with a 260px floor so it never gets too short. */
@media (max-width: 991px) { .tdc-banner-stage { height: auto; aspect-ratio: 16/9; min-height: 260px; } }
@media (max-width: 600px) { .tdc-banner-stage { aspect-ratio: 16/9; min-height: 240px; } }

/* Caption stays anchored centre-left on every breakpoint so it always
   sits on top of the image. Padding tightens on smaller screens. */
.tdc-banner-caption { padding: 0 8% 0 6%; align-items: center; text-align: left; }
.tdc-banner-caption-inner { max-width: 520px; }
@media (max-width: 600px) {
  .tdc-banner-caption { padding: 0 6% 0 5%; }
  .tdc-banner-caption-inner { max-width: 75%; }
}

/* Legibility gradient — left-fade everywhere; tightened on mobile so
   the caption side keeps its readability even with a shallower image. */
.tdc-banner-gradient { background: linear-gradient(90deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.18) 45%, rgba(0,0,0,0) 70%); }
@media (max-width: 600px) {
  .tdc-banner-gradient { background: linear-gradient(90deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.05) 90%); }
}
`;

// Stage and layers force their own GPU composite layer (translateZ +
// will-change) so the browser doesn't repaint on every frame of
// opacity/transform/clip-path tweens. Height is set via the
// .tdc-banner-stage class above so the media query can swap desktop
// (viewport-fill) vs mobile (16:5 aspect) cleanly.
const stage = {
  position: "relative",
  width: "100%",
  overflow: "hidden",
  background: "#0a0a0a",
  transform: "translateZ(0)",
  WebkitFontSmoothing: "antialiased",
};
const layer = {
  position: "absolute",
  inset: 0,
  overflow: "hidden",
  willChange: "opacity, transform, filter, clip-path",
  transform: "translateZ(0)",
  backfaceVisibility: "hidden",
};
const imgStyle = { width: "100%", height: "100%", objectFit: "cover", display: "block", transform: "translateZ(0)" };
// One easing curve used everywhere so the transitions feel related, not
// like a grab-bag of separate effects.
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

function Slot({ slide, onClick, style }) {
  const cursor = slide?.link && slide.link !== "undefined" ? "pointer" : "default";
  const handleClick = () => slide?.link && slide.link !== "undefined" && onClick?.(slide);
  const hasCaption = !!(slide?.title || slide?.subtitle);
  return (
    <div style={{ position: "absolute", inset: 0, cursor }} onClick={handleClick}>
      <img src={slide.src} alt="" style={{ ...imgStyle, ...style }} />
      {hasCaption && (
        <>
          {/* Soft gradient so the text reads on any image */}
          <div className="tdc-banner-gradient" style={{
            position: "absolute", inset: 0,
            pointerEvents: "none",
          }} />
          <div className="tdc-banner-caption" style={{
            position: "absolute", inset: 0,
            display: "flex",
            pointerEvents: "none",
            color: "#fff", fontFamily: "Poppins, sans-serif",
          }}>
            <div className="tdc-banner-caption-inner">
              {slide.title && (
                <h2 style={{
                  margin: 0,
                  fontSize: "clamp(28px, 4.6vw, 64px)",
                  fontWeight: 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  lineHeight: 1.05,
                  textShadow: "0 2px 22px rgba(0,0,0,0.4)",
                }}>{slide.title}</h2>
              )}
              {slide.title && slide.subtitle && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "18px 0" }}>
                  <span style={{ display: "inline-block", width: "min(40vw, 220px)", height: 1, background: "#C5A028" }} />
                  <span style={{ color: "#C5A028", fontSize: 13, lineHeight: 1 }}>✦</span>
                  <span style={{ display: "inline-block", flex: 1, maxWidth: 0 }} />
                </div>
              )}
              {slide.subtitle && (
                <p style={{
                  margin: 0,
                  fontSize: "clamp(14px, 1.6vw, 20px)",
                  fontWeight: 300,
                  letterSpacing: "0.4px",
                  lineHeight: 1.45,
                  color: "#f3eed8",
                  maxWidth: "22ch",
                  textShadow: "0 1px 14px rgba(0,0,0,0.4)",
                }}>{slide.subtitle}</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── 1. Crossfade ─────────────────────────────────────────────────────
function Crossfade({ slides, intervalMs, onClick }) {
  const i = useAutoIndex(slides.length, intervalMs);
  return (
    <div className="tdc-banner-stage" style={stage}>
      {slides.map((s, n) => (
        <div key={n} style={{ ...layer, opacity: n === i ? 1 : 0, transition: `opacity 1.2s ${EASE}` }}>
          <Slot slide={s} onClick={onClick} />
        </div>
      ))}
    </div>
  );
}

// ── 2. Ken Burns ─────────────────────────────────────────────────────
function KenBurns({ slides, intervalMs, onClick }) {
  const ms = Math.max(intervalMs, 6000);
  const i = useAutoIndex(slides.length, ms);
  const dir = i % 2 === 0 ? "bannerKbA" : "bannerKbB";
  return (
    <div className="tdc-banner-stage" style={stage}>
      {slides.map((s, n) => (
        <div key={n} style={{ ...layer, opacity: n === i ? 1 : 0, transition: `opacity 1.2s ${EASE}` }}>
          <Slot slide={s} onClick={onClick} style={{ animation: n === i ? `${dir} ${ms / 1000 + 1}s ease-out forwards` : "none", willChange: "transform" }} />
        </div>
      ))}
    </div>
  );
}

// ── 3. Parallax ──────────────────────────────────────────────────────
function Parallax({ slides, intervalMs, onClick }) {
  const i = useAutoIndex(slides.length, intervalMs);
  return (
    <div className="tdc-banner-stage" style={stage}>
      {slides.map((s, n) => {
        const offset = (n - i) * 100;
        return (
          <div key={n} style={{ ...layer, transform: `translate3d(${offset}%, 0, 0)`, transition: `transform 1s ${EASE}` }}>
            <Slot slide={s} onClick={onClick} />
          </div>
        );
      })}
    </div>
  );
}

// ── 4. Cinematic Zoom ────────────────────────────────────────────────
function CinematicZoom({ slides, intervalMs, onClick }) {
  const i = useAutoIndex(slides.length, intervalMs);
  return (
    <div className="tdc-banner-stage" style={stage}>
      {slides.map((s, n) => (
        <div
          key={n}
          style={{
            ...layer,
            opacity: n === i ? 1 : 0,
            transform: n === i ? "scale(1) translateZ(0)" : "scale(1.08) translateZ(0)",
            transition: `opacity 1.2s ${EASE}, transform 1.2s ${EASE}`,
          }}
        >
          <Slot slide={s} onClick={onClick} />
        </div>
      ))}
    </div>
  );
}

// ── 5. Curtain Split ─────────────────────────────────────────────────
function CurtainSplit({ slides, intervalMs, onClick }) {
  const i = useAutoIndex(slides.length, intervalMs);
  return (
    <div className="tdc-banner-stage" style={stage}>
      {slides.map((s, n) => {
        const active = n === i;
        return (
          <div key={n} style={{ ...layer, clipPath: active ? "inset(0 0 0 0)" : "inset(50% 0 50% 0)", transition: `clip-path 1.1s ${EASE}`, zIndex: active ? 2 : 1 }}>
            <Slot slide={s} onClick={onClick} />
          </div>
        );
      })}
    </div>
  );
}

// ── 6. Diagonal Wipe ─────────────────────────────────────────────────
function DiagonalWipe({ slides, intervalMs, onClick }) {
  const i = useAutoIndex(slides.length, intervalMs);
  return (
    <div className="tdc-banner-stage" style={stage}>
      {slides.map((s, n) => {
        const active = n === i;
        return (
          <div
            key={n}
            style={{
              ...layer,
              clipPath: active ? "polygon(0 0, 100% 0, 100% 100%, 0 100%)" : "polygon(100% 0, 100% 0, 100% 100%, 100% 100%)",
              transition: `clip-path 1.1s ${EASE}`,
              zIndex: active ? 2 : 1,
            }}
          >
            <Slot slide={s} onClick={onClick} />
          </div>
        );
      })}
    </div>
  );
}

// ── 7. Mosaic Tiles ──────────────────────────────────────────────────
function MosaicTiles({ slides, intervalMs, onClick }) {
  const i = useAutoIndex(slides.length, intervalMs);
  const prevRef = useRef(i);
  const [outgoing, setOutgoing] = useState(null);
  useEffect(() => {
    if (prevRef.current !== i) {
      setOutgoing(prevRef.current);
      const id = setTimeout(() => setOutgoing(null), 1700);
      prevRef.current = i;
      return () => clearTimeout(id);
    }
  }, [i]);

  const cols = 6, rows = 4;
  const cells = Array.from({ length: cols * rows });

  return (
    <div className="tdc-banner-stage" style={stage}>
      <div style={layer}><Slot slide={slides[i]} onClick={onClick} /></div>
      {outgoing != null && slides[outgoing] && (
        <div style={{ position: "absolute", inset: 0, zIndex: 2, display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}>
          {cells.map((_, c) => {
            const col = c % cols;
            const row = Math.floor(c / cols);
            return (
              <div
                key={c}
                style={{
                  backgroundImage: `url(${slides[outgoing].src})`,
                  backgroundSize: `${cols * 100}% ${rows * 100}%`,
                  backgroundPosition: `${(col / (cols - 1)) * 100}% ${(row / (rows - 1)) * 100}%`,
                  animation: "bannerMosaicIn 1.4s ease-out forwards",
                  animationDelay: `${(col + row) * 70}ms`,
                  transformOrigin: "center",
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 8. Spotlight ─────────────────────────────────────────────────────
function Spotlight({ slides, intervalMs, onClick }) {
  const i = useAutoIndex(slides.length, intervalMs);
  return (
    <div className="tdc-banner-stage" style={stage}>
      {slides.map((s, n) => {
        const active = n === i;
        return (
          <div key={n} style={{ ...layer, clipPath: active ? "circle(140% at 50% 50%)" : "circle(0% at 50% 50%)", transition: `clip-path 1.2s ${EASE}`, zIndex: active ? 2 : 1 }}>
            <Slot slide={s} onClick={onClick} />
          </div>
        );
      })}
    </div>
  );
}

// ── 9. Blur Fade ─────────────────────────────────────────────────────
function BlurFade({ slides, intervalMs, onClick }) {
  const i = useAutoIndex(slides.length, intervalMs);
  return (
    <div className="tdc-banner-stage" style={stage}>
      {slides.map((s, n) => (
        <div
          key={n}
          style={{
            ...layer,
            opacity: n === i ? 1 : 0,
            filter: n === i ? "blur(0px)" : "blur(16px)",
            transform: n === i ? "scale(1) translateZ(0)" : "scale(1.06) translateZ(0)",
            transition: `opacity 1.1s ${EASE}, filter 1.1s ${EASE}, transform 1.1s ${EASE}`,
          }}
        >
          <Slot slide={s} onClick={onClick} />
        </div>
      ))}
    </div>
  );
}

// ── 10. 3D Card Flip ─────────────────────────────────────────────────
function CardFlip({ slides, intervalMs, onClick }) {
  const i = useAutoIndex(slides.length, intervalMs);
  return (
    <div className="tdc-banner-stage" style={{ ...stage, perspective: "1400px" }}>
      {slides.map((s, n) => {
        const active = n === i;
        const prev = (i - 1 + slides.length) % slides.length === n;
        return (
          <div
            key={n}
            style={{
              ...layer,
              transformStyle: "preserve-3d",
              transform: active ? "rotateY(0deg)" : prev ? "rotateY(-180deg)" : "rotateY(180deg)",
              opacity: active || prev ? 1 : 0,
              transition: `transform 1.1s ${EASE}, opacity 1.1s ${EASE}`,
              backfaceVisibility: "hidden",
            }}
          >
            <Slot slide={s} onClick={onClick} />
          </div>
        );
      })}
    </div>
  );
}

const TRANSITION_MAP = {
  crossfade: Crossfade,
  kenBurns: KenBurns,
  parallax: Parallax,
  cinematicZoom: CinematicZoom,
  curtainSplit: CurtainSplit,
  diagonalWipe: DiagonalWipe,
  mosaicTiles: MosaicTiles,
  spotlight: Spotlight,
  blurFade: BlurFade,
  cardFlip: CardFlip,
};
